import { Inject, Injectable } from "@angular/core";
import { patchState, signalStore, withState } from "@ngrx/signals";
import { TranslateService } from "@ngx-translate/core";
import { jwtDecode } from "jwt-decode";
import { KeycloakService } from "keycloak-angular";
import { firstValueFrom } from "rxjs";
import { keycloakInitializer } from "../configs/keycloack.config";
import { COUNTRY_LANG_MAP, type CountryCode, type CountryLang } from "../models/i18n/country.constant";
import type { IEnvironment } from "../models/interfaces/environment.interface";
import { UserModel } from "../models/user/user.model";
import { ENVIRONMENT } from "../modules/keycloak.module";
import { IndexedDBService } from "../services/index-db.service";
import { UserService } from "../services/user.service";
import { withStorage } from "./features/with-storage.feature";

export type UserState = {
  user: UserModel;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  jwt: string;
};

const initialState: UserState = {
  user: {
    commerce: "",
    country: "",
    exp: 0,
    iat: 0,
    email: "",
    lastname: "",
    rol: {
      name: "",
    },
    permissions: [],
    names: "",
    secondLastname: "",
    storeCode: "",
    storeName: "",
  },
  isAuthenticated: false,
  isAuthenticating: false,
  jwt: "",
};

@Injectable({ providedIn: "root" })
export class UserStore extends signalStore(
  { protectedState: false },
  withState(initialState),
  withStorage("user", sessionStorage, {
    loadExcludeKeys: ["isAuthenticating"],
    saveExcludeKeys: ["isAuthenticating"],
  })
) {
  private logoutTimer: number | null = null;
  private keycloakInitPromise: Promise<boolean> | undefined;

  getUser(): UserModel {
    return this.user();
  }

  getJwt(): string {
    return this.jwt();
  }

  constructor(
    private readonly keycloakService: KeycloakService,
    private readonly userService: UserService,
    private readonly indexDbService: IndexedDBService,
    private readonly translate: TranslateService,
    @Inject(ENVIRONMENT) private readonly appEnvironment: IEnvironment
  ) {
    super();
    this.scheduleTokenExpirationLogout();
  }

  async checkAuthenticated(): Promise<boolean> {
    await this.isTokenExpired();
    if (this.hasValidJwtSession()) {
      await this.setLanguageFromUserCountry(this.user());
      this.scheduleTokenExpirationLogout();
      return true;
    }

    return false;
  }

  async loginWithKeycloak(redirectUri: string): Promise<boolean> {
    patchState(this, { isAuthenticating: true });
    try {
      await this.ensureKeycloakInitialized();

      if (!this.keycloakService.isLoggedIn()) {
        await this.keycloakService.login({ redirectUri });
        return false;
      }

      const token = await this.keycloakService.getToken();
      if (!token) {
        await this.keycloakService.login({ redirectUri });
        return false;
      }

      const tokenSecurity = await firstValueFrom(this.userService.authenticate(token));
      if (!tokenSecurity?.accessToken) {
        await this.keycloakService.login({ redirectUri });
        return false;
      }

      const userSecurity = jwtDecode<UserModel>(tokenSecurity.accessToken);
      await this.setLanguageFromUserCountry(userSecurity);
      patchState(this, { user: userSecurity, isAuthenticated: true, jwt: tokenSecurity.accessToken });
      this.scheduleTokenExpirationLogout();
      return true;
    } catch (error) {
      console.error("Failed to load user", error);
      await this.keycloakService.login({ redirectUri });
      return false;
    } finally {
      patchState(this, { isAuthenticating: false });
    }
  }

  private async setLanguageFromUserCountry(user: UserModel | null): Promise<void> {
    if (!user) return;

    const rawCode = user.country?.toUpperCase();
    const countryCode = rawCode as CountryCode;

    const targetLang: CountryLang = COUNTRY_LANG_MAP[countryCode] ?? this.appEnvironment.DEFAULT_LANG;

    if (this.translate.currentLang !== targetLang) {
      await firstValueFrom(this.translate.use(targetLang)).catch(async () => {
        if (this.translate.currentLang !== this.appEnvironment.DEFAULT_LANG) {
          await firstValueFrom(this.translate.use(this.appEnvironment.DEFAULT_LANG)).catch(() => void 0);
        }
      });
    }
  }

  private async isTokenExpired(): Promise<void> {
    const user = this.user();
    const currentTime = Math.floor(Date.now() / 1000);
    if (user.exp && currentTime >= user.exp) {
      console.warn("Token has expired. Logging out...");
      await this.logoutAndClearData();
    }
  }

  async logoutAndClearData(): Promise<void> {
    try {
      patchState(this, {
        user: initialState.user,
        isAuthenticated: false,
        isAuthenticating: false,
        jwt: "",
      });
      this.clearSessionStorage();
      await this.indexDbService.clearAll();
      await this.logoutFromKeycloak();
    } catch (error) {
      console.error("Error during logout and clear data:", error);
    }
  }

  private clearSessionStorage(): void {
    sessionStorage.clear();
  }

  private scheduleTokenExpirationLogout(): void {
    clearTimeout(this.logoutTimer ?? 0);

    const user = this.user();
    const currentTime = Math.floor(Date.now() / 1000);

    if (user.exp && user.exp > currentTime) {
      const timeUntilExpiration = (user.exp - currentTime) * 1000;

      this.logoutTimer = window.setTimeout(() => {
        this.logoutAndClearData().catch((error: unknown) => {
          console.error("Failed to execute scheduled logout", error);
        });
      }, timeUntilExpiration);
    }
  }

  private hasValidJwtSession(): boolean {
    const user = this.user();
    const currentTime = Math.floor(Date.now() / 1000);

    return !!(this.isAuthenticated() && this.jwt() && user.exp && user.exp > currentTime);
  }

  private async ensureKeycloakInitialized(): Promise<boolean> {
    this.keycloakInitPromise ??= keycloakInitializer(this.keycloakService, this.appEnvironment)();
    return await this.keycloakInitPromise;
  }

  private async logoutFromKeycloak(): Promise<void> {
    try {
      await this.ensureKeycloakInitialized();
      await this.keycloakService.logout();
    } catch (error) {
      console.error("Failed to logout from Keycloak", error);
    }
  }

  getSessionRemainingTimeMs(): number {
    const user = this.user();
    if (!user.exp) {
      return 0;
    }
    const currentTime = Math.floor(Date.now() / 1000);
    const timeRemaining = (user.exp - currentTime) * 1000;

    return timeRemaining > 0 ? timeRemaining : 0;
  }
}
